import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
// Render backend as the primary API (no Vercel serverless); VITE_API_URL
// overrides the origin for local development. See config/api.ts.
import { API_BASE_URL, RENDER_URL } from "../config/api";
import { attachActor } from "./actor";

// Transport for every backend call: the shared apiClient, its interceptors,
// and the switch to VITE_API_URL_FALLBACK when Render is unreachable.
//
// Every request goes to the backend. The old "direct Supabase" mode, which
// pointed apiClient at Supabase REST in local dev (and could be switched on in
// any build by VITE_DEBUG_API), is gone; the few reads that use Supabase on
// purpose do so explicitly (see supabaseRestClient below and config/supabase).

// Extended type for Axios request config with our custom properties
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  retryCount?: number;
}

// Flag to track if Render.com server is down
let renderServerDown = false;

const API_FALLBACK_URL = import.meta.env.VITE_API_URL_FALLBACK || "";
// Set VITE_DEBUG_API=true in .env to enable verbose request logging.
const DEBUG_API = import.meta.env.VITE_DEBUG_API === "true";
export const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "";
export const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || "";

// An axios instance for the backend that names the acting team member
// (X-DBR-Actor, api/actor.ts) on every request.
const backendAxios = (config: Parameters<typeof axios.create>[0]): AxiosInstance => {
  const instance = axios.create(config);
  instance.interceptors.request.use(attachActor);
  return instance;
};

// A backend client without the URL-rewriting interceptors (it still carries
// the actor header). Points at the fallback API when Render is known to be down.
export const createClient = () => {
  try {
    // If Render server is known to be down, use the fallback API
    if (renderServerDown && API_FALLBACK_URL && API_BASE_URL.includes('render.com')) {
      return backendAxios({
        baseURL: API_FALLBACK_URL,
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 30000,
      });
    }

    return backendAxios({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
      timeout: 30000,
    });
  } catch (error) {
    console.error("Error creating API client:", error);
    // Fallback to a basic client if there's an error
    return backendAxios({
      baseURL: API_FALLBACK_URL || API_BASE_URL,
      timeout: 30000,
    });
  }
};

/**
 * A client for Supabase REST (PostgREST) with the anon key, for the reads that
 * query Supabase directly on purpose. Created per call, as those reads always
 * did.
 */
export const supabaseRestClient = () =>
  axios.create({
    baseURL: SUPABASE_URL,
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${SUPABASE_KEY}`,
    },
    timeout: 30000,
  });

// Every apiClient instance - the first one and each one swapped in when the
// Render health check fails - is built here, so each carries the interceptors.
// They used to be installed once, on the first instance only, and a swapped-in
// client sent its requests with no URL rewriting at all.
function createApiClient(): AxiosInstance {
  return installInterceptors(createClient());
}

// Initialize the API client with the appropriate configuration
export let apiClient = createApiClient();

// Function to proactively check if Render is accessible
const checkRenderServer = async () => {
  if (API_BASE_URL.includes('render.com')) {
    try {
      // Simple ping request with a short timeout
      const response = await fetch(`${RENDER_URL}/api/health`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(3000) // 3 second timeout
      });

      if (response.ok) {
        renderServerDown = false;
      } else {
        console.warn('Render server returned error status, switching to fallback');
        renderServerDown = true;
        // Recreate client with fallback
        apiClient = createApiClient();
      }
    } catch {
      console.warn('Render server is not accessible, switching to fallback');
      renderServerDown = true;
      // Recreate client with fallback
      apiClient = createApiClient();
    }
  }
};

// Check Render server on startup
checkRenderServer();

// Helper function to format API URL paths
const formatApiPath = (path: string) => {
  // If the path already includes /api/ prefix, don't modify it
  if (path.startsWith("/api/")) {
    return path;
  }

  // Check if the base URL already has /api in its path
  const hasApiInBaseUrl = API_BASE_URL.includes('/api');

  // If base URL already contains /api, don't add it again
  if (hasApiInBaseUrl) {
    // Just ensure the path starts with a slash
    return path.startsWith("/") ? path : `/${path}`;
  } else {
    // Add /api prefix to the path
    return path.startsWith("/") ? `/api${path}` : `/api/${path}`;
  }
};

// Install the request/response interceptors on an axios instance. Called for
// every apiClient instance (see createApiClient), so a client swapped in at
// runtime behaves exactly like the first one.
function installInterceptors(instance: AxiosInstance): AxiosInstance {
  // Add request interceptor for debugging and path conversion
  instance.interceptors.request.use(
    (config) => {
      // OAuth, auth and these custom backend paths always get an absolute
      // backend URL, even when the API base points at Supabase.
      const customApiPaths = ['/oauth/', '/auth/', '/saved-remarks/', '/price-suggestions', '/blacklist'];
      const isCustomApiPath = config.url && customApiPaths.some(path => config.url?.includes(path));

      if (isCustomApiPath) {
        // Force these requests to always use the API backend, not Supabase.
        // Build an absolute URL to the backend API
        let pathUrl = config.url || '';

        // Ensure the URL has /api/ prefix
        if (!pathUrl.includes('/api/')) {
          // Add /api/ prefix if not present
          pathUrl = pathUrl.startsWith('/') ? `/api${pathUrl}` : `/api/${pathUrl}`;
        }

        // Create absolute URL to ensure it doesn't use Supabase baseURL.
        //
        // Strip a trailing /api before joining: pathUrl already carries one from
        // the block above, and API_BASE_URL has ended in /api since 9f321b96c
        // ("route all API requests through Render"). The two composed into
        //   https://connective-dbr.onrender.com/api + /api/saved-remarks/suggest
        // which 404s. Live: single /api returns 200, double returns 404. That
        // broke every path in customApiPaths - saved-remarks suggest/track/popular,
        // the whole blacklist page, price-suggestions and the oauth/auth paths -
        // and surfaced to users as "Failed to fetch remark suggestions".
        const backendOrigin = API_BASE_URL.includes('supabase.co')
          ? RENDER_URL
          : API_BASE_URL.replace(/\/api\/?$/, '');
        const backendUrl = backendOrigin;
        config.url = `${backendUrl}${pathUrl}`;

        // Clear the baseURL so Axios uses the absolute URL from config.url
        config.baseURL = '';

        // Remove Supabase headers if they exist
        if (config.headers) {
          delete config.headers['apikey'];
          delete config.headers['Authorization'];
        }

        return config;
      }

      // Regular path handling for non-OAuth URLs
      if (config.url) {
        // Debug output to understand URL transformation
        if (DEBUG_API || import.meta.env.DEV) {
          console.log(`Request interceptor - Original URL: ${config.url}`);
        }

        // For API access with test endpoints, don't modify the URL
        if (config.url.includes("/test/")) {
          if (DEBUG_API || import.meta.env.DEV) {
            console.log(`Test endpoint detected, not modifying URL: ${config.url}`);
          }
        } else {
          // For regular endpoints, ensure proper formatting
          const originalUrl = config.url;
          config.url = formatApiPath(config.url);

          if (DEBUG_API || import.meta.env.DEV) {
            console.log(`URL transformed: ${originalUrl} -> ${config.url}`);
          }
        }
      }

      // Log request details in non-production environments or if debugging is enabled
      if (import.meta.env.DEV || localStorage.getItem("debug_api") === "true") {
        console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`, {
          baseURL: config.baseURL,
          headers: config.headers,
          params: config.params,
          data: config.data,
          fullUrl: `${config.baseURL}${config.url}`
        });
      }
      return config;
    },
    (error) => {
      console.error("API Request Error:", error);
      return Promise.reject(error);
    }
  );

  // Add response interceptor for better error handling and automatic fallback
  instance.interceptors.response.use(
    (response) => {
      // Log response details in non-production environments
      if (import.meta.env.DEV || localStorage.getItem("debug_api") === "true") {
        console.log(`API Response: ${response.status} ${response.config.url}`, {
          data: response.data,
          headers: response.headers,
        });
      }

      // If we get a successful response from Render, reset the flag
      if (response.config.baseURL?.includes('render.com')) {
        renderServerDown = false;
      }

      return response;
    },
    async (error) => {
      console.error("API Response Error:", {
        url: error.config?.url,
        method: error.config?.method,
        baseURL: error.config?.baseURL,
        error: error.message,
      });

      // Handle CORS errors and connection failures
      if (
        (error.message && error.message.includes("Network Error")) ||
        (error.response && error.response.status === 0) ||
        (!error.response && error.request)
      ) {
        // Check if we're using Render and need to switch to fallback
        if (error.config?.baseURL?.includes('render.com') ||
            (error.config?.baseURL === API_BASE_URL && API_BASE_URL.includes('render.com'))) {
          renderServerDown = true;

          // Create a warning message for users
          console.warn("Render server appears to be down, switching to fallback API");

          // If this is not a retry attempt, try with direct API
          const config = error.config as ExtendedAxiosRequestConfig;
          if (!config._retry && API_FALLBACK_URL) {
            config._retry = true;
            config.baseURL = API_FALLBACK_URL;

            try {
              // Create a new client with the fallback URL
              const fallbackClient = backendAxios({
                baseURL: API_FALLBACK_URL,
                headers: {
                  "Content-Type": "application/json",
                },
                timeout: 30000,
              });

              // Return the retry attempt with the fallback client
              return fallbackClient(config);
            } catch (retryError) {
              console.error("Error retrying with fallback:", retryError);
            }
          }
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
}
